import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject
} from "react";
import { Link } from "react-router-dom";
import type {
  FrameworkSnapshot,
  ResolvedPartnerCapabilityMapping,
  ResolvedPartnerCapabilityMappingNode
} from "@apf/framework-config";
import {
  capabilityMapAnchor,
  capabilityMapHref,
  derivePartnerCapabilityCoverage,
  partnerCapabilitiesInArea,
  partnerCapabilityAreas,
  type CapabilityCoverage,
  type CapabilityCoverageStatus
} from "../capabilityMapping";
import { ordinal, StatusMark } from "./ui";

type Copy = (key: string, fallback?: string) => string;

export interface CapabilityOverviewProps {
  snapshot: FrameworkSnapshot;
  mapping: ResolvedPartnerCapabilityMapping;
  copy: Copy;
  selectedAreaId?: string;
  selectedCapabilityId?: string;
  onSelectedCapabilityChange?: (id?: string, areaId?: string) => void;
}

interface CapabilityOverviewRecord {
  node: ResolvedPartnerCapabilityMappingNode;
  coverage: CapabilityCoverage;
}

interface CapabilityAreaRecord {
  area: ResolvedPartnerCapabilityMappingNode;
  records: CapabilityOverviewRecord[];
}

const narrowOverviewQuery = "(max-width: 42rem)";
const narrowOverviewWidthRem = 40;
const coverageStatuses = ["reference", "pattern", "roadmap", "unmapped"] as const;

function useMediaQuery(queryText: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia(queryText).matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(queryText);
    const sync = () => setMatches(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [queryText]);
  return matches;
}

/**
 * The map is also shipped inside the portable Workblock. Its semantic
 * composition therefore follows the component's available width rather than
 * assuming that the viewport and the map have the same dimensions.
 */
function useNarrowOverview(): [RefObject<HTMLElement | null>, boolean] {
  const ref = useRef<HTMLElement | null>(null);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia(narrowOverviewQuery).matches
  );
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const sync = (width: number) => setIsNarrow(width < narrowOverviewWidthRem * rootFontSize);
    sync(element.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries.find(({ target }) => target === element);
      if (entry) sync(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, isNarrow];
}

function coverageLabel(copy: Copy, status: CapabilityCoverageStatus): string {
  if (status === "reference") return copy("status.reference.chip");
  if (status === "pattern") return copy("status.pattern.chip");
  return copy(`partner-capability.overview.status.${status}.label`);
}

function coverageCounts(records: Iterable<CapabilityOverviewRecord>): Record<CapabilityCoverageStatus, number> {
  const counts: Record<CapabilityCoverageStatus, number> = {
    reference: 0,
    pattern: 0,
    roadmap: 0,
    unmapped: 0
  };
  for (const { coverage } of records) counts[coverage.status] += 1;
  return counts;
}

function capabilityCount(copy: Copy, count: number): string {
  return `${count} ${copy(count === 1
    ? "partner-capability.count.singular"
    : "partner-capability.count.plural")}`;
}

function CapabilityCell({
  record,
  copy,
  selected,
  onSelect
}: {
  record: CapabilityOverviewRecord;
  copy: Copy;
  selected: boolean;
  onSelect: (trigger: HTMLButtonElement) => void;
}) {
  const { node, coverage } = record;
  const label = copy(node.labelKey);
  return (
    <li className="apf-capability-overview__capability">
      <button
        className={`apf-capability-overview__cell apf-capability-overview__cell--${coverage.status}`}
        type="button"
        data-capability-id={node.id}
        aria-controls={selected ? "capability-overview-detail" : undefined}
        aria-expanded={selected}
        aria-pressed={selected}
        aria-label={`${copy("partner-capability.overview.select")}: ${label}. ${coverageLabel(copy, coverage.status)}.${selected ? ` ${copy("partner-capability.overview.selected")}.` : ""}`}
        onClick={(event) => onSelect(event.currentTarget)}
      >
        <span className="apf-capability-overview__capability-name">{label}</span>
        <span className="apf-capability-overview__cell-footer">
          <span className="apf-capability-overview__coverage-status">
            {coverageLabel(copy, coverage.status)}
          </span>
          {selected ? (
            <span className="apf-capability-overview__selection">
              {copy("partner-capability.overview.selected")}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

function MappingTargetList({
  items,
  emptyLabel
}: {
  items: Array<{ id: string; label: string; to: string; status?: string; statusValue?: string }>;
  emptyLabel: string;
}) {
  if (!items.length) return <p className="apf-capability-overview__none">{emptyLabel}</p>;
  return (
    <ul className="apf-capability-overview__target-list">
      {items.map((item) => (
        <li key={item.id}>
          <Link to={item.to}>{item.label}</Link>
          {item.status && item.statusValue ? <StatusMark value={item.statusValue} label={item.status} /> : null}
        </li>
      ))}
    </ul>
  );
}

function CapabilityDetail({
  record,
  snapshot,
  mapping,
  copy,
  onClose,
  variant = "panel"
}: {
  record: CapabilityOverviewRecord;
  snapshot: FrameworkSnapshot;
  mapping: ResolvedPartnerCapabilityMapping;
  copy: Copy;
  onClose: () => void;
  variant?: "panel" | "inline";
}) {
  const { node, coverage } = record;
  const domainById = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  const sharedById = new Map(snapshot.content.capabilities.map((capability) => [capability.id, capability]));
  const statusById = new Map(snapshot.content.businessModuleStatusDisplay.map((status) => [status.id, status]));
  const domains = node.groundworks.domainWorkblockIds.flatMap((id) => {
    const domain = domainById.get(id);
    return domain ? [{
      id,
      label: copy(domain.nameKey),
      to: capabilityMapHref({ view: "groundworks", mapId: mapping.id, anchor: capabilityMapAnchor("domain-workblock", id) })
    }] : [];
  });
  const modules = coverage.modules.map((module) => {
    const status = statusById.get(module.implementationStatus);
    return {
      id: module.id,
      label: copy(module.labelKey),
      status: status ? copy(status.chipKey) : module.implementationStatus,
      statusValue: module.implementationStatus,
      to: capabilityMapHref({ view: "groundworks", mapId: mapping.id, anchor: capabilityMapAnchor("business-module", module.id) })
    };
  });
  const shared = node.groundworks.sharedCapabilityIds.flatMap((id) => {
    const capability = sharedById.get(id);
    return capability ? [{
      id,
      label: copy(capability.labelKey),
      to: capabilityMapHref({ view: "groundworks", mapId: mapping.id, anchor: capabilityMapAnchor("shared-capability", id) })
    }] : [];
  });

  return (
    <aside
      className={`apf-capability-overview__detail apf-capability-overview__detail--${variant}`}
      id="capability-overview-detail"
      aria-labelledby="capability-overview-detail-title"
      aria-live="polite"
    >
      <header className="apf-capability-overview__detail-header">
        <div>
          <p>{copy(`partner-capability.fit.${node.groundworks.fit}`)}</p>
          <h3 id="capability-overview-detail-title">{copy(node.labelKey)}</h3>
          <p>{copy(`partner-capability.fit.${node.groundworks.fit}.description`)}</p>
        </div>
        <div className="apf-capability-overview__detail-coverage">
          <span>{copy("partner-capability.overview.detail.coverage")}</span>
          <strong className={`apf-capability-overview__coverage-status apf-capability-overview__coverage-status--${coverage.status}`}>
            {coverageLabel(copy, coverage.status)}
          </strong>
        </div>
      </header>

      {coverage.isMixed ? (
        <p className="apf-capability-overview__limitation">
          {copy("partner-capability.overview.detail.mixed")}
        </p>
      ) : null}
      {!coverage.hasModuleTarget ? (
        <p className="apf-capability-overview__limitation">
          {copy("partner-capability.overview.detail.no-module")}
        </p>
      ) : coverage.status === "roadmap" ? (
        <p className="apf-capability-overview__limitation">
          {copy("partner-capability.overview.detail.roadmap-only")}
        </p>
      ) : null}

      <div className="apf-capability-overview__detail-groups">
        <section aria-labelledby="capability-overview-domains">
          <h4 id="capability-overview-domains">{copy("partner-capability.overview.detail.domains")}</h4>
          <MappingTargetList items={domains} emptyLabel={copy("partner-capability.overview.detail.none")} />
        </section>
        <section aria-labelledby="capability-overview-modules">
          <h4 id="capability-overview-modules">{copy("partner-capability.overview.detail.modules")}</h4>
          <MappingTargetList items={modules} emptyLabel={copy("partner-capability.overview.detail.none")} />
        </section>
        <section aria-labelledby="capability-overview-shared">
          <h4 id="capability-overview-shared">{copy("partner-capability.overview.detail.shared")}</h4>
          <MappingTargetList items={shared} emptyLabel={copy("partner-capability.overview.detail.none")} />
        </section>
      </div>

      <div className="apf-capability-overview__detail-actions">
        <Link
          className="apf-capability-overview__detail-link"
          to={capabilityMapHref({
            view: "partner",
            mapId: mapping.id,
            anchor: capabilityMapAnchor("capability", node.id)
          })}
        >
          {copy("partner-capability.overview.detail.open")}
        </Link>
        <button
          className="apf-capability-overview__detail-close"
          type="button"
          onClick={onClose}
        >
          {copy("partner-capability.overview.detail.close")}
        </button>
      </div>
    </aside>
  );
}

function CapabilityArea({
  item,
  copy,
  selectedAreaId,
  selectedId,
  onSelect,
  onClose,
  collapsible,
  gridRowCount,
  inlineDetail,
  snapshot,
  mapping
}: {
  item: CapabilityAreaRecord;
  copy: Copy;
  selectedAreaId?: string;
  selectedId?: string;
  onSelect: (id: string, trigger: HTMLButtonElement) => void;
  onClose: () => void;
  collapsible: boolean;
  gridRowCount?: number;
  inlineDetail: boolean;
  snapshot: FrameworkSnapshot;
  mapping: ResolvedPartnerCapabilityMapping;
}) {
  const { area, records } = item;
  const label = copy(area.labelKey);
  const crossCutting = area.kind === "cross-cutting-area";
  const containsSelection = records.some(({ node }) => node.id === selectedId);
  const [isOpen, setIsOpen] = useState(containsSelection || selectedAreaId === area.id);

  useEffect(() => {
    if (collapsible && (containsSelection || selectedAreaId === area.id)) setIsOpen(true);
  }, [collapsible, containsSelection, selectedAreaId, area.id]);

  const className = `apf-capability-overview__area${crossCutting ? " apf-capability-overview__area--cross-cutting" : ""}`;
  const heading = (
    <>
      <span className="apf-capability-overview__area-order">{ordinal(area.order)}</span>
      <span className="apf-capability-overview__area-name" role="heading" aria-level={3}>{label}</span>
      <span className="apf-capability-overview__area-count">
        {capabilityCount(copy, records.length)}
      </span>
    </>
  );
  const capabilities = (
    <ol className="apf-capability-overview__capabilities">
      {records.map((record) => (
        <Fragment key={record.node.id}>
          <CapabilityCell
            record={record}
            copy={copy}
            selected={record.node.id === selectedId}
            onSelect={(trigger) => onSelect(record.node.id, trigger)}
          />
          {inlineDetail && record.node.id === selectedId ? (
            <li className="apf-capability-overview__inline-detail">
              <CapabilityDetail
                record={record}
                snapshot={snapshot}
                mapping={mapping}
                copy={copy}
                onClose={onClose}
                variant="inline"
              />
            </li>
          ) : null}
        </Fragment>
      ))}
      {Array.from({ length: Math.max(0, (gridRowCount ?? records.length) - records.length) }, (_, index) => (
        <li
          aria-hidden="true"
          className="apf-capability-overview__capability apf-capability-overview__capability--filler"
          key={`filler-${index}`}
        />
      ))}
    </ol>
  );

  if (collapsible) return (
    <details
      className={className}
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary aria-label={`${copy("partner-capability.overview.area.toggle")}: ${label}`}>
        {heading}
      </summary>
      {capabilities}
    </details>
  );

  return (
    <section className={className} aria-label={label}>
      <header className="apf-capability-overview__area-header">{heading}</header>
      {capabilities}
    </section>
  );
}

/**
 * The complete governed Accenture capability topology. Selection adds one
 * mapping record without filtering or removing any of the 49 capabilities.
 */
export function CapabilityOverview({
  snapshot,
  mapping,
  copy,
  selectedAreaId,
  selectedCapabilityId,
  onSelectedCapabilityChange
}: CapabilityOverviewProps) {
  const [overviewRef, isNarrow] = useNarrowOverview();
  const isPrint = useMediaQuery("print");
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);
  const records = useMemo(() => new Map(
    mapping.nodes
      .filter((node) => node.kind === "capability")
      .map((node) => [node.id, { node, coverage: derivePartnerCapabilityCoverage(snapshot, node) }])
  ), [mapping, snapshot]);
  const areaRecords = useMemo(() => partnerCapabilityAreas(mapping).map((area) => ({
    area,
    records: partnerCapabilitiesInArea(mapping, area.id).flatMap((node) => {
      const record = records.get(node.id);
      return record ? [record] : [];
    })
  })), [mapping, records]);
  const [selectedId, setSelectedId] = useState<string | undefined>(
    selectedCapabilityId && records.has(selectedCapabilityId) ? selectedCapabilityId : undefined
  );

  useEffect(() => {
    setSelectedId(selectedCapabilityId && records.has(selectedCapabilityId) ? selectedCapabilityId : undefined);
  }, [records, selectedCapabilityId]);

  const lifecycleAreas = areaRecords.filter(({ area }) => area.kind === "area");
  const crossCuttingAreas = areaRecords.filter(({ area }) => area.kind === "cross-cutting-area");
  const lifecycleRowCount = Math.max(1, ...lifecycleAreas.map(({ records: areaCapabilities }) => areaCapabilities.length));
  const counts = coverageCounts(records.values());
  const selected = selectedId ? records.get(selectedId) : undefined;
  const inlineDetail = isNarrow && !isPrint;

  const selectCapability = (id: string, trigger: HTMLButtonElement) => {
    lastTriggerRef.current = trigger;
    const next = selectedId === id ? undefined : id;
    const areaId = records.get(id)?.node.parentId;
    setSelectedId(next);
    onSelectedCapabilityChange?.(next, areaId);
  };

  const closeDetail = () => {
    if (!selected) return;
    const trigger = lastTriggerRef.current
      ?? overviewRef.current?.querySelector<HTMLButtonElement>(
        `.apf-capability-overview__cell[data-capability-id="${selected.node.id}"]`
      )
      ?? null;
    setSelectedId(undefined);
    onSelectedCapabilityChange?.(undefined, selected.node.parentId);
    const restoreFocus = () => trigger?.focus();
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(restoreFocus);
    else setTimeout(restoreFocus, 0);
  };

  useEffect(() => {
    if (!inlineDetail || !selectedId) return;
    const selectedCell = overviewRef.current?.querySelector<HTMLElement>(
      ".apf-capability-overview__cell[aria-pressed='true']"
    );
    if (selectedCell && typeof selectedCell.scrollIntoView === "function") {
      selectedCell.scrollIntoView({ block: "start" });
    }
  }, [inlineDetail, selectedId]);

  return (
    <section className="apf-capability-overview" aria-labelledby="capability-overview-title" ref={overviewRef}>
      <header className="apf-capability-overview__header">
        <div>
          <p>{copy("partner-capability.overview.eyebrow")}</p>
          <h2 id="capability-overview-title">{copy("partner-capability.overview.title")}</h2>
          <p>{copy("partner-capability.overview.description")}</p>
        </div>
        <ul className="apf-capability-overview__legend" aria-label={copy("partner-capability.overview.legend.label")}>
          {coverageStatuses.map((status) => (
            <li className={`apf-capability-overview__legend-item apf-capability-overview__legend-item--${status}`} key={status}>
              <strong>{coverageLabel(copy, status)}</strong>
              <span>{capabilityCount(copy, counts[status])}</span>
            </li>
          ))}
        </ul>
      </header>

      <div
        className="apf-capability-overview__lifecycle"
        style={{ "--apf-capability-overview-lifecycle-rows": lifecycleRowCount } as CSSProperties}
      >
        {lifecycleAreas.map((item) => (
          <CapabilityArea
            key={item.area.id}
            item={item}
            copy={copy}
            selectedAreaId={selectedAreaId}
            selectedId={selectedId}
            onSelect={selectCapability}
            onClose={closeDetail}
            collapsible={inlineDetail}
            gridRowCount={lifecycleRowCount}
            inlineDetail={inlineDetail}
            snapshot={snapshot}
            mapping={mapping}
          />
        ))}
      </div>
      <div className="apf-capability-overview__cross-cutting">
        {crossCuttingAreas.map((item) => (
          <CapabilityArea
            key={item.area.id}
            item={item}
            copy={copy}
            selectedAreaId={selectedAreaId}
            selectedId={selectedId}
            onSelect={selectCapability}
            onClose={closeDetail}
            collapsible={inlineDetail}
            inlineDetail={inlineDetail}
            snapshot={snapshot}
            mapping={mapping}
          />
        ))}
      </div>

      {!inlineDetail && selected ? (
        <CapabilityDetail
          record={selected}
          snapshot={snapshot}
          mapping={mapping}
          copy={copy}
          onClose={closeDetail}
        />
      ) : null}
    </section>
  );
}
